import { Code, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import { useUpsertProductPriceForm } from '../hooks/useUpsertPriceForm';
import type {
	ProductPriceDto,
	UpsertProductPriceDto,
} from '../types/ProductPriceDto';
import { UpsertProductPriceForm } from './UpsertProductPriceForm';

interface EditProductModalProps {
	close: () => void;
	isOpened: boolean;
	updateProductPriceRecord: (product: ProductPriceDto) => void;
	productPrice: UpsertProductPriceDto;
	branchId: string;
	encodedSkuid: string;
}

export function EditProductPriceModal({
	isOpened,
	close,
	updateProductPriceRecord,
	productPrice,
	branchId,
	encodedSkuid,
}: EditProductModalProps) {
	const { organisation } = useParams();

	const successModal = useModal();

	const { form, inputs } = useUpsertProductPriceForm(productPrice);

	const newProductPriceMutation = mutate<
		UpsertProductPriceDto,
		ProductPriceDto
	>(`${organisation}/product-price/edit/${branchId}/${encodedSkuid}`, inputs);

	const resetAndClose = () => {
		form.reset();

		close();
		successModal.close();
	};

	const complete = () => {
		if (newProductPriceMutation.data?.value) {
			updateProductPriceRecord(newProductPriceMutation.data.value);

			form.setInitialValues(newProductPriceMutation.data.value);
			resetAndClose();
		}
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={
					<>
						<Title label={'Edit Product Price'} />
						<Code>
							Adding or deleting branch stocks can be done in the product view.
						</Code>
					</>
				}
				withCloseButton={true}
				closeButtonProps={{
					style: { alignSelf: 'flex-start' },
				}}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={newProductPriceMutation.isLoading} />
				<SmallModal
					isOpen={successModal.isOpen}
					close={complete}
					title={'Product Price Edited!'}
					body={`"${inputs.skuid}" has been edited. Click Ok to continue...`}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newProductPriceMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<UpsertProductPriceForm form={form} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
